import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Search, Loader2, Languages } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { ImageUploader } from '../../components/ImageUploader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get full image URL
const getImageUrl = (img) => {
  if (!img) return 'https://via.placeholder.com/40?text=No+img';
  if (img.startsWith('http://') || img.startsWith('https://')) {
    return img;
  }
  if (img.startsWith('/api/uploads/') || img.startsWith('/uploads/')) {
    const path = img.startsWith('/uploads/') ? img.replace('/uploads/', '/api/uploads/') : img;
    return `${API_URL}${path}`;
  }
  return img;
};

const emptyProduct = {
  name: '',
  description: '',
  category_id: '',
  price_retail: '',
  price_wholesale: '',
  stock: '',
  weight: '0.1',
  length: '10',
  width: '10',
  height: '5',
  images: [''],
  is_bestseller: false,
  is_new: true,
  is_active: true
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [formData, setFormData] = useState(emptyProduct);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [prodsRes, catsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/categories`)
      ]);
      setProducts(prodsRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditProduct(product);
      setFormData({
        ...product,
        weight: product.weight || 0.1,
        length: product.length || 10,
        width: product.width || 10,
        height: product.height || 5,
        images: product.images?.length > 0 ? product.images : ['']
      });
    } else {
      setEditProduct(null);
      setFormData(emptyProduct);
    }
    setDialogOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || '' : value
    }));
  };

  const handleImageChange = (index, value) => {
    const newImages = [...formData.images];
    newImages[index] = value;
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const addImageField = () => {
    setFormData(prev => ({ ...prev, images: [...prev.images, ''] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      const data = {
        ...formData,
        images: formData.images.filter(img => img.trim() !== ''),
        price_retail: parseFloat(formData.price_retail),
        price_wholesale: parseFloat(formData.price_wholesale),
        stock: parseInt(formData.stock)
      };

      if (editProduct) {
        await axios.put(`${API_URL}/api/products/${editProduct.product_id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Produit mis à jour');
      } else {
        await axios.post(`${API_URL}/api/products`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Produit créé');
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Produit supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleTranslateAll = async () => {
    if (!window.confirm('Traduire tous les produits et catégories en anglais et espagnol ? Cette opération peut prendre plusieurs minutes.')) return;
    
    setTranslating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/admin/translate-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Traduction terminée: ${res.data.products_translated} produits, ${res.data.categories_translated} catégories`);
      if (res.data.errors?.length > 0) {
        console.warn('Translation errors:', res.data.errors);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de traduction');
    } finally {
      setTranslating(false);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-products">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl text-brand-black">Produits</h1>
            <p className="text-brand-dark/60">{products.length} produits en catalogue</p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={handleTranslateAll} 
              variant="outline" 
              className="border-brand-gold text-brand-gold hover:bg-brand-gold hover:text-white"
              disabled={translating}
              data-testid="translate-all-btn"
            >
              {translating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Languages className="h-4 w-4 mr-2" />}
              {translating ? 'Traduction...' : 'Traduire tout'}
            </Button>
            <Button onClick={() => handleOpenDialog()} className="bg-brand-gold text-white hover:bg-brand-gold" data-testid="add-product-btn">
              <Plus className="h-4 w-4 mr-2" /> Ajouter un produit
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-dark/50" />
          <Input
            placeholder="Rechercher un produit..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-brand-white border-brand-gold/20 text-brand-black"
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-brand-white border border-brand-gold/20 rounded shadow-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-brand-gold/20 hover:bg-brand-cream">
                  <TableHead className="text-brand-dark/80">Produit</TableHead>
                  <TableHead className="text-brand-dark/80">Catégorie</TableHead>
                  <TableHead className="text-brand-dark/80">Prix détail</TableHead>
                  <TableHead className="text-brand-dark/80">Poids</TableHead>
                  <TableHead className="text-brand-dark/80">Stock</TableHead>
                  <TableHead className="text-brand-dark/80">Statut</TableHead>
                  <TableHead className="text-brand-dark/80 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map(product => (
                  <TableRow key={product.product_id} className="border-brand-gold/20 hover:bg-brand-cream">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <img 
                          src={getImageUrl(product.images?.[0])} 
                          alt={product.name}
                          className="w-10 h-10 object-cover rounded"
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/40?text=No+img'; }}
                        />
                        <div>
                          <p className="text-brand-black font-medium">{product.name}</p>
                          <div className="flex gap-1">
                            {product.is_new && <Badge className="bg-brand-gold text-white text-xs">Nouveau</Badge>}
                            {product.is_bestseller && <Badge className="bg-brand-gold text-white text-xs">Best</Badge>}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-brand-dark/80">
                      {categories.find(c => c.category_id === product.category_id)?.name || '-'}
                    </TableCell>
                    <TableCell className="text-brand-black font-medium">${product.price_retail.toFixed(2)} CAD</TableCell>
                    <TableCell className="text-brand-dark/80">{(product.weight || 0.1).toFixed(2)} kg</TableCell>
                    <TableCell>
                      <span className={product.stock < 10 ? 'text-orange-500 font-medium' : 'text-brand-dark'}>
                        {product.stock}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={product.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                        {product.is_active ? 'Actif' : 'Inactif'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(product)} className="text-brand-dark/60 hover:text-brand-gold">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(product.product_id)} className="text-brand-dark/60 hover:text-red-500">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Product Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-brand-white border-brand-gold/20 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-brand-black">
                {editProduct ? 'Modifier le produit' : 'Nouveau produit'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-brand-dark">Nom du produit</Label>
                  <Input name="name" value={formData.name} onChange={handleChange} required className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black" />
                </div>
                
                <div className="sm:col-span-2">
                  <Label className="text-brand-dark">Description</Label>
                  <Textarea name="description" value={formData.description} onChange={handleChange} required rows={3} className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black resize-none" />
                </div>

                <div>
                  <Label className="text-brand-dark">Catégorie</Label>
                  <Select value={formData.category_id} onValueChange={(v) => setFormData(prev => ({ ...prev, category_id: v }))}>
                    <SelectTrigger className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent className="bg-brand-white border-brand-gold/20">
                      {categories.map(cat => (
                        <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-brand-dark">Stock</Label>
                  <Input name="stock" type="number" value={formData.stock} onChange={handleChange} required className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black" />
                </div>

                <div>
                  <Label className="text-brand-dark">Poids (kg)</Label>
                  <Input name="weight" type="number" step="0.01" min="0.01" value={formData.weight} onChange={handleChange} required className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black" placeholder="ex: 0.5" />
                </div>

                <div>
                  <Label className="text-brand-dark">Prix détail ($ CAD)</Label>
                  <Input name="price_retail" type="number" step="0.01" value={formData.price_retail} onChange={handleChange} required className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black" />
                </div>

                <div>
                  <Label className="text-brand-dark">Prix gros ($ CAD)</Label>
                  <Input name="price_wholesale" type="number" step="0.01" value={formData.price_wholesale} onChange={handleChange} required className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black" />
                </div>
              </div>

              {/* Dimensions */}
              <div>
                <Label className="text-brand-dark font-semibold">Dimensions du produit (cm)</Label>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  <div>
                    <Label className="text-brand-dark/70 text-sm">Longueur</Label>
                    <Input name="length" type="number" step="0.1" min="1" value={formData.length} onChange={handleChange} required className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black" placeholder="10" />
                  </div>
                  <div>
                    <Label className="text-brand-dark/70 text-sm">Largeur</Label>
                    <Input name="width" type="number" step="0.1" min="1" value={formData.width} onChange={handleChange} required className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black" placeholder="10" />
                  </div>
                  <div>
                    <Label className="text-brand-dark/70 text-sm">Hauteur</Label>
                    <Input name="height" type="number" step="0.1" min="1" value={formData.height} onChange={handleChange} required className="mt-1 bg-brand-white border-brand-gold/20 text-brand-black" placeholder="5" />
                  </div>
                </div>
              </div>

              {/* Images */}
              <div>
                <Label className="text-brand-dark">Images du produit</Label>
                <div className="mt-2">
                  <ImageUploader 
                    images={formData.images.filter(img => img.trim() !== '')} 
                    onChange={(newImages) => setFormData(prev => ({ ...prev, images: newImages }))}
                    maxImages={5}
                  />
                </div>
              </div>

              {/* Checkboxes */}
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={formData.is_new} onCheckedChange={(c) => setFormData(prev => ({ ...prev, is_new: c }))} />
                  <span className="text-brand-dark/80">Nouveau</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={formData.is_bestseller} onCheckedChange={(c) => setFormData(prev => ({ ...prev, is_bestseller: c }))} />
                  <span className="text-brand-dark/80">Best-seller</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={formData.is_active} onCheckedChange={(c) => setFormData(prev => ({ ...prev, is_active: c }))} />
                  <span className="text-brand-dark/80">Actif</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="text-brand-dark/60">
                  Annuler
                </Button>
                <Button type="submit" disabled={saving} className="bg-brand-gold text-white hover:bg-brand-gold">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editProduct ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
